import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import {
  FeatureAnalysisRowDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import { AnalysisDecisionStatus } from "@models/platform-analysis/enums/feature/decisionStatus";
import { FeatureAnalysisCollapseStateFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisCollapseLevelFilter";
import {
  Column,
  ColumnDef,
  createColumnHelper,
  ExpandedState,
} from "@tanstack/react-table";
import { isEmpty } from "lodash";
import { RenderedProductHeaderCell } from "./components/rendered-items/header-cells/product";
import { RenderedSubProductHeaderCell } from "./components/rendered-items/header-cells/sub-product";
import { RenderedProductCellContainer } from "./components/rendered-items/rendered-cell-containers/product";
import { RenderedSubProductCellContainer } from "./components/rendered-items/rendered-cell-containers/sub-product";
import { RenderedProductRowTitleCell } from "./components/rendered-items/row-title-cells/product";
import { RenderedSubProductRowTitleCell } from "./components/rendered-items/row-title-cells/sub-product";
import { RenderedProductTableTitle } from "./components/rendered-items/table-titles/product";
import { RenderedSubProductTableTitle } from "./components/rendered-items/table-titles/sub-product";
import {
  TableProductCellColorMapping,
  TableProductCellDecisionBannerMapping,
  TableRenderedCellBackgroundColorMapping,
  TableVirtualizationConfigs as Configs,
} from "./configs";

const ColumnHelper = createColumnHelper<FeatureAnalysisRowDTO>();

export const getTableColumnDefs = ({
  selectedProducts,
  selectedSubProducts,
  isSupplierGroup,
  title,
  hiddenColumnIndexesMap,
}: {
  selectedProducts?: SelectedProductDTO<AnalysisDecisionStatus>[];
  selectedSubProducts?: SelectedSubProductDTO[];
  isSupplierGroup?: boolean;
  title?: string;
  hiddenColumnIndexesMap?: boolean[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Tanstack table default type
}): ColumnDef<FeatureAnalysisRowDTO, any>[] => {
  if (!isEmpty(selectedProducts))
    return getSelectedProductsColumnDef({
      selectedProducts,
      isSupplierGroup,
      title,
    });

  return getSelectedSubProductsColumnDef({
    selectedSubProducts,
    title,
    hiddenColumnIndexesMap: hiddenColumnIndexesMap || [],
  });
};

export const getTableVirtualizationConfigs = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Tanstack table default type
  columns: Column<FeatureAnalysisRowDTO, any>[],
) => {
  const isNotFireFox =
    typeof window !== "undefined" &&
    navigator.userAgent.indexOf("Firefox") === -1;

  const rowOverscan = isNotFireFox
    ? Configs.DefaultRowOverscan
    : Configs.FireFoxRowOverscan;
  const columnOverscan = isNotFireFox
    ? Configs.DefaultColumnOverscan
    : Configs.FireFoxColumnOverscan;

  const measureElementHeight = isNotFireFox
    ? (element: Element) => element?.getBoundingClientRect().height
    : undefined;
  const measureElementWidth = isNotFireFox
    ? (element: Element) => element?.getBoundingClientRect().width
    : undefined;

  const rowEstimateSizeFunction = () => Configs.DefaultRowHeight;
  const columnEstimateSizeFunction = (index: number): number =>
    columns[index]?.getSize() ?? Configs.DefaultColumnWidth;

  return {
    rowOverscan,
    columnOverscan,
    measureElementHeight,
    measureElementWidth,
    rowEstimateSizeFunction,
    columnEstimateSizeFunction,
  };
};

export const getNewTableCollapseStateByLevel = ({
  collapseLevel = FeatureAnalysisCollapseStateFilterLevel.FeatureLevel,
  currentData,
}: {
  collapseLevel?: FeatureAnalysisCollapseStateFilterLevel;
  currentData?: FeatureAnalysisRowDTO[];
}) => {
  if (!currentData) return {};

  const initialCollapseState: ExpandedState = {};

  // Determine expand state based on collapse level
  const shouldExpandSection =
    collapseLevel === FeatureAnalysisCollapseStateFilterLevel.FeatureLevel ||
    collapseLevel === FeatureAnalysisCollapseStateFilterLevel.SubGroupLevel;

  const shouldExpandSubSection =
    collapseLevel === FeatureAnalysisCollapseStateFilterLevel.FeatureLevel;

  currentData.forEach((section, sectionIndex) => {
    initialCollapseState[sectionIndex] = shouldExpandSection;

    section.subRows?.forEach((_, subSectionIndex) => {
      initialCollapseState[`${sectionIndex}.${subSectionIndex}`] =
        shouldExpandSubSection;
    });
  });

  return initialCollapseState;
};

export const getProductDecisionStatusColor = ({
  product,
  isSupplierGroup,
  forTableCell,
}: {
  product: SelectedProductDTO;
  isSupplierGroup?: boolean;
  forTableCell?: boolean;
}): string | undefined => {
  const { decisionStatus, isMissingEssential } = product || {};
  const status = !isSupplierGroup
    ? getDefaultStatus(decisionStatus, isMissingEssential)
    : isMissingEssential
    ? AnalysisDecisionStatus.MissingEssential
    : AnalysisDecisionStatus.Undecided;

  return forTableCell
    ? TableRenderedCellBackgroundColorMapping[status]
    : TableProductCellColorMapping[status];
};

export const getFeeSubProductStatusColor = () =>
  TableProductCellColorMapping[AnalysisDecisionStatus.Undecided];

export const getProductDecisionBannerConfigs = ({
  decisionStatus,
  isMissingEssential,
}: {
  decisionStatus?: AnalysisDecisionStatus | null;
  isMissingEssential?: boolean;
}) => {
  const status = getDefaultStatus(decisionStatus, isMissingEssential);

  return TableProductCellDecisionBannerMapping[status];
};

// #region Private functions
const getDefaultStatus = (
  originalStatus?: AnalysisDecisionStatus | null,
  isMissingEssential?: boolean,
) =>
  (originalStatus === AnalysisDecisionStatus.Undecided || !originalStatus) &&
  isMissingEssential
    ? AnalysisDecisionStatus.MissingEssential
    : originalStatus ?? AnalysisDecisionStatus.Undecided;

const getSelectedProductsColumnDef = ({
  selectedProducts,
  isSupplierGroup,
  title,
}: {
  selectedProducts?: SelectedProductDTO[];
  isSupplierGroup?: boolean;
  title?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Tanstack table default type
}): ColumnDef<FeatureAnalysisRowDTO, any>[] => {
  if (!selectedProducts) return [];
  const numOfSelectedProducts = selectedProducts.length;
  const titleCellWidth =
    numOfSelectedProducts > Configs.NumOfItemsToCenter
      ? Configs.TitleColumnWidth
      : Configs.ExpandTitleColumnWidth;
  const followingCellsWidth =
    numOfSelectedProducts > Configs.NumOfItemsToCenter
      ? Configs.DefaultColumnWidth
      : Configs.ExpandColumnWidth;

  const staticCol = ColumnHelper.accessor((section) => section, {
    id: "feature-name",
    header: () => (
      <RenderedProductTableTitle title={title} fixedWidth={titleCellWidth} />
    ),
    cell: ({ row: { original } }) => (
      <RenderedProductRowTitleCell rowData={original} />
    ),
    size: titleCellWidth,
  });

  const flexCols = selectedProducts.map((product, index) => {
    return ColumnHelper.accessor((section) => section, {
      id: `product-${index}`,
      header: () => (
        <RenderedProductHeaderCell
          product={product}
          fixedWidth={followingCellsWidth}
        />
      ),
      cell: ({ row: { original } }) => {
        const analysisData = original?.analysisData?.[index];

        return (
          <RenderedProductCellContainer
            analysisData={analysisData}
            level={original.level}
            name={original.name}
            forceCellColor={getProductDecisionStatusColor({
              product,
              isSupplierGroup,
              forTableCell: true,
            })}
          />
        );
      },
      size: followingCellsWidth,
    });
  });

  return [staticCol, ...flexCols];
};

const getSelectedSubProductsColumnDef = ({
  selectedSubProducts,
  title,
  hiddenColumnIndexesMap,
}: {
  selectedSubProducts?: SelectedSubProductDTO[];
  title?: string;
  hiddenColumnIndexesMap: boolean[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Tanstack table default type
}): ColumnDef<FeatureAnalysisRowDTO, any>[] => {
  if (!selectedSubProducts) return [];

  const staticCol = ColumnHelper.accessor((section) => section, {
    id: "feature-name",
    header: () => (
      <RenderedSubProductTableTitle
        title={title}
        fixedWidth={Configs.TitleColumnWidth}
      />
    ),
    cell: ({ row: { original } }) => (
      <RenderedSubProductRowTitleCell rowData={original} />
    ),
    size: Configs.TitleColumnWidth,
  });

  const flexCols: ColumnDef<FeatureAnalysisRowDTO, FeatureAnalysisRowDTO>[] =
    [];

  selectedSubProducts.forEach((subProduct, index) => {
    if (hiddenColumnIndexesMap[index]) return;

    const col = ColumnHelper.accessor((section) => section, {
      id: `product-${index}`,
      header: () => (
        <RenderedSubProductHeaderCell
          subProduct={subProduct}
          fixedWidth={Configs.SubProductColumnWidth}
        />
      ),
      cell: ({ row: { original } }) => {
        const analysisData = original?.MAAnalysisData?.[index];

        return (
          <RenderedSubProductCellContainer
            analysisData={analysisData}
            level={original.level}
            variableName={original.variableName}
          />
        );
      },
      size: Configs.SubProductColumnWidth,
    });

    flexCols.push(col);
  });

  // const flexCols = selectedSubProducts.map((subProduct, index) => {
  //   return ColumnHelper.accessor((section) => section, {
  //     id: `product-${index}`,
  //     header: () => (
  //       <RenderedSubProductHeaderCell
  //         subProduct={subProduct}
  //         fixedWidth={Configs.SubProductColumnWidth}
  //       />
  //     ),
  //     cell: ({ row: { original } }) => {
  //       const analysisData = original?.MAAnalysisData?.[index];
  //       return (
  //         <RenderedSubProductCellContainer
  //           analysisData={analysisData}
  //           level={original.level}
  //           variableName={original.variableName}
  //         />
  //       );
  //     },
  //     size: Configs.SubProductColumnWidth,
  //   });
  // });

  return [staticCol, ...flexCols];
};
// #endregion Private functions
